import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, Heart, Droplet } from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../../config/api';
import Overview from './Overview';
import Profile from './Profile';
import AvailableRequests from './AvailableRequests';
import AcceptedRequests from './AcceptedRequests';
import DonationHistory from './DonationHistory';
import DonorActivity from './DonorActivity';

const DonorDashboard = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState({
    donor: {
      name: '',
      bloodType: '',
      lastDonation: null,
      totalDonations: 0,
      donationStreak: 0,
      nextEligibleDate: new Date().toISOString()
    },
    donationHistory: [],
    acceptedRequests: [],
    nearbyRequests: [],
    upcomingAppointments: [],
    upcomingEvents: [],
    impactMetrics: {
      totalDonations: 0,
      livesSaved: 0,
      hospitalsServed: 0,
      successRate: 0
    },
    monthlyDonations: Array(12).fill(0),
    achievements: [],
    yoyGrowth: 0
  });

  useEffect(() => {
    fetchDonorDashboard();
  }, []);

  const fetchDonorDashboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/dashboard/donor');
      setDashboardData(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching donor dashboard:', err);
      toast.error('Failed to load dashboard data');
      setLoading(false);
    }
  };

  // Handle certificate download
  const handleDownloadCertificate = async (donation) => {
    try {
      // Show loading toast
      toast.info('Preparing your certificate for download...', {
        position: 'top-right',
        autoClose: 2000,
      });

      // In a real implementation, we would make an API call to get the certificate
      // For example:
      // const response = await api.get(`/donors/certificate/${donation.donationId}`, { responseType: 'blob' });
      
      // For now, we'll simulate a delay and then show a success message
      setTimeout(() => {
        // Success toast
        toast.success('Certificate downloaded successfully!', {
          position: 'top-right',
          autoClose: 3000,
        });
        
        // In a real implementation, we would create a download link for the blob
        // const url = window.URL.createObjectURL(new Blob([response.data]));
        // const link = document.createElement('a');
        // link.href = url;
        // link.setAttribute('download', `BloodHero_Certificate_${donation.donationId}.pdf`);
        // document.body.appendChild(link);
        // link.click();
        // link.remove();
      }, 1500);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      toast.error('Failed to download certificate. Please try again later.', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  // Get current section based on URL
  const getCurrentSection = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'overview';
    if (path.includes('/dashboard/available')) return 'available';
    if (path.includes('/dashboard/accepted')) return 'accepted';
    if (path.includes('/dashboard/history')) return 'history';
    if (path.includes('/dashboard/profile')) return 'profile';
    if (path.includes('/dashboard/activity')) return 'activity';
    return 'overview';
  };

  // Render the appropriate content based on the current route
  const renderContent = () => {
    const section = getCurrentSection();
    
    switch (section) {
      case 'overview':
        return <Overview />;
      case 'available':
        return <AvailableRequests nearbyRequests={dashboardData.nearbyRequests} loading={loading} />;
      case 'accepted':
        return <AcceptedRequests acceptedRequests={dashboardData.acceptedRequests} loading={loading} />;
      case 'history':
        return <DonationHistory 
          donationHistory={dashboardData.donationHistory} 
          loading={loading} 
          handleDownloadCertificate={handleDownloadCertificate} 
        />;
      case 'profile':
        return <Profile donor={dashboardData.donor} loading={loading} />;
      case 'activity':
        return <DonorActivity 
          dashboardData={dashboardData}
          loading={loading}
        />;
      default:
        return <Overview />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Fixed Header */}
      {/* <div className="fixed top-0 left-0 sm:left-[80px] md:left-[280px] right-0 z-30 p-4 sm:px-6 pt-6 bg-gray-50">
        <div className="bg-gradient-to-r from-red-600 to-red-700 rounded-xl shadow-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 sm:gap-0">
            <div className="flex items-center gap-3">
              <Heart className="h-6 w-6 sm:h-8 sm:w-8 text-white animate-pulse" />
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-white">Donor Dashboard</h1>
                <p className="text-red-100 text-xs sm:text-sm">Making a difference, one donation at a time</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white/10 px-3 py-1 sm:px-4 sm:py-2 rounded-lg">
              <Droplet className="h-4 w-4 sm:h-5 sm:w-5 text-red-200" />
              <span className="text-white text-sm font-medium">{dashboardData.donor.bloodType}</span>
            </div>
          </div>
        </div>
      </div> */}

      {/* Main Content Container */}
      <div className="relative pt-6 md:pt-8 px-4 sm:px-6 pb-6">
        {renderContent()}
      </div>
    </div>
  );
};

export default DonorDashboard;